import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Spin } from "antd";
import { setHeaderMenuData } from "../../actions/headerMenu/headerMenu";
import adminToolsService from "../../services/admin-tools.service";

// Blocks a single app's page by direct URL when the dealer's Application
// Permissions don't have that app enabled (ticket #1793 reopened, point 1:
// typing /applicationPreviewGemStoneLink still opened the app for a retailer
// without the permission). Each route passes its own `permKey`
// ("diamondLink" / "gemstoneLink" / ...) and only that flag is checked, so
// disabling one app can never block another app's route (point 3/4 of the
// same ticket) the way a shared/hardcoded check would.
//
// Always re-fetches fresh on mount rather than trusting whatever is already
// in Redux — same reasoning as AllApps.js's own comment: permissions are
// otherwise only fetched at login and persisted, so a tab left open (or a
// stale redux-persist value) would keep showing the old allowed/blocked
// state after an admin flips the toggle. The fetch's .finally() always
// clears the spinner — success, failure, or a "not Success" response all
// resolve loading, so this can't get stuck spinning.
const AppPermissionGuard = ({ permKey, children }) => {
  const dispatch = useDispatch();
  const loginDetials = useSelector((state) => state.loginReducer);
  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;

  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    if (!dealerId) {
      // Redux/login not hydrated yet — effect re-runs once dealerId shows up.
      return;
    }
    let cancelled = false;
    setLoading(true);
    adminToolsService
      .GetAllMenuDtlsByPermission({ dealerId: dealerId.toString() })
      .then((response) => {
        if (cancelled) return;
        if (response.data.message === "Success") {
          dispatch(setHeaderMenuData(response.data.responseData));
          setPermissions(
            response.data.responseData.applicationPermisstionResponse || {}
          );
        } else {
          // Fail closed on an unexpected response, not "keep whatever we had".
          setPermissions({});
        }
      })
      .catch(() => {
        if (!cancelled) setPermissions({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId]);

  if (loading) {
    return (
      <div style={{ padding: "4rem", textAlign: "center" }}>
        <Spin spinning />
      </div>
    );
  }

  // Fail closed: a missing/undefined key (permission never granted) blocks
  // the page, same as sidemenu.js's left-nav check for these same flags.
  if (!permissions[permKey]) {
    return <Navigate to="/allapps" replace />;
  }

  return children;
};

export default AppPermissionGuard;
